import React, { useMemo } from 'react';

import { Area } from '@ant-design/plots';
import { Empty } from 'antd';

import type { TimelinePoint } from '../hooks/useDashboardStats';

type Props = {
  timeline: TimelinePoint[];
};

const SERIES_ORDERS = 'Orders';
const SERIES_RETURNS = 'Returns';

/** Compact "Sep 14" style tick, locale-stable (ISO YYYY-MM-DD in → en-GB out). */
function formatTickDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
}

export const TrendAreaChart = ({ timeline }: Props) => {
  const data = useMemo(() => {
    const rows: { date: string; series: string; value: number }[] = [];
    timeline.forEach((d) => {
      rows.push({ date: d.date, series: SERIES_ORDERS, value: d.orders });
      rows.push({ date: d.date, series: SERIES_RETURNS, value: d.suivi });
    });
    return rows;
  }, [timeline]);

  /** Sparser X-axis on long windows so labels don't collide. */
  const xTickCount = useMemo(() => {
    if (timeline.length <= 7) return timeline.length;
    if (timeline.length <= 31) return 8;
    return 10;
  }, [timeline.length]);

  if (!data.length) {
    return <Empty description="No data for this period yet" />;
  }

  return (
    <Area
      data={data}
      xField="date"
      yField="value"
      colorField="series"
      shapeField="smooth"
      autoFit
      height={300}
      style={{ fillOpacity: 0.35 }}
      scale={{
        color: { range: ['#1677ff', '#fa8c16'] },
      }}
      axis={{
        x: {
          tickCount: xTickCount,
          labelFormatter: (d: string) => formatTickDate(d),
          labelAutoRotate: false,
        },
        y: {
          labelFormatter: (v: number) => String(v),
          gridLineDash: [2, 2],
        },
      }}
      legend={{
        color: { position: 'top', layout: { justifyContent: 'center' } },
      }}
      tooltip={{
        title: (d: { date: string }) => formatTickDate(d.date),
      }}
    />
  );
};
